/**
 * The AI layer as the screens see it: whether it is on and which AI functions this user has
 * (`/ai/status`), the assistant panel's open state and the context it opens with, and the small
 * calls the screens make (summarise, draft, translate, extract, search).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api.ts';
import { useSession } from './session.tsx';

export interface AiStatus { enabled: boolean; mode: 'anthropic' | 'mock' | 'disabled'; reason: string | null; model: string | null; dataSharing: 'full' | 'masked'; functions: string[] }
export interface AiContextInfo { path?: string; entity?: string; recordId?: string; title?: string }
export interface Proposal { kind: 'record' | 'screen'; entity?: string; fields?: Record<string, unknown>; lines?: Array<{ entity: string; fields: Record<string, unknown> }>; path?: string; label: string; reason?: string; issues?: Array<{ field: string; message: string }> }
export interface ChatReply { conversationId: string; messageId: string; reply: string; proposals: Proposal[]; toolCalls: Array<{ name: string; input: Record<string, unknown>; ok: boolean }>; usage: { inputTokens: number; outputTokens: number; calls: number }; model: string }

interface AiApi {
  status: AiStatus | null;
  /** true when the provider is on and the user may use this function (ai.<code>) */
  can: (fn: string) => boolean;
  open: boolean; context: AiContextInfo | null; prefill: string;
  openAssistant: (context?: AiContextInfo, prefill?: string) => void; closeAssistant: () => void;
  chat: (message: string, conversationId: string | null, context?: AiContextInfo) => Promise<ChatReply>;
  summarize: (entity: string, id: string, focus?: string) => Promise<{ summary: string }>;
  draft: (input: { entity?: string; id?: string; field?: string; purpose: string; instruction?: string; language?: string }) => Promise<{ text: string }>;
  translate: (text: string, to: 'en' | 'ar') => Promise<{ text: string }>;
  extract: (text: string, entity: string, hint?: string) => Promise<{ lines: Array<Record<string, unknown>>; issues: string[] }>;
  search: (text: string) => Promise<{ path: string; explanation: string; entity: string }>;
}

const AiCtx = createContext<AiApi | null>(null);
export const PROPOSAL_KEY = 'ai-proposal';

export function AiProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState<AiContextInfo | null>(null);
  const [prefill, setPrefill] = useState('');
  useEffect(() => {
    if (!session) { setStatus(null); return; }
    let cancelled = false;
    api.get<AiStatus>('ai/status').then((s) => { if (!cancelled) setStatus(s); }).catch(() => { if (!cancelled) setStatus({ enabled: false, mode: 'disabled', reason: null, model: null, dataSharing: 'full', functions: [] }); });
    return () => { cancelled = true; };
  }, [session]);
  const can = useCallback((fn: string) => !!status?.enabled && status.functions.includes(`ai.${fn}`), [status]);
  const value = useMemo<AiApi>(() => ({
    status, can, open, context, prefill,
    openAssistant: (c, text) => { setContext(c ?? null); setPrefill(text ?? ''); setOpen(true); },
    closeAssistant: () => setOpen(false),
    chat: (message, conversationId, c) => api.post<ChatReply>('ai/chat', { message, conversationId, context: c ?? undefined }),
    summarize: (entity, id, focus) => api.post('ai/summarize', { entity, id, focus }),
    draft: (input) => api.post('ai/draft', input),
    translate: (text, to) => api.post('ai/translate', { text, to }),
    extract: (text, entity, hint) => api.post('ai/extract', { text, entity, hint }),
    search: (text) => api.post('ai/search', { text }),
  }), [status, can, open, context, prefill]);
  return <AiCtx.Provider value={value}>{children}</AiCtx.Provider>;
}

export function useAi(): AiApi {
  const v = useContext(AiCtx);
  if (!v) throw new Error('useAi outside AiProvider');
  return v;
}

/** hands a proposal to the form page: header fields as defaults, lines created after the save */
export function stashProposal(p: Proposal): void {
  try { sessionStorage.setItem(PROPOSAL_KEY, JSON.stringify(p)); } catch { /* the form still opens, without lines */ }
}
export function takeProposal(): Proposal | null {
  try { const raw = sessionStorage.getItem(PROPOSAL_KEY); if (!raw) return null; sessionStorage.removeItem(PROPOSAL_KEY); return JSON.parse(raw) as Proposal; } catch { return null; }
}
